// docStore.js — generische Dokument-Stores (jsonb) für Rezepte, Bewertungen,
// Rechnungen, Team und Chat.
//
// Muster wie aptStore: LIVE Supabase / DEMO localStorage, mit Cache-Spiegel
// unter dem bekannten localStorage-Key, damit synchrone Leser (Dashboard-
// Zähler, metrics.js) unverändert funktionieren. Zeilen tragen Client-IDs;
// in der Tabelle liegt der Rest als data-jsonb.
import { supabase } from "./supabase";
import { serverActive } from "./mode";

function makeDocStore(table, cacheKey) {
  const loadCache = () => {
    try { const s = localStorage.getItem(cacheKey); if (s) return JSON.parse(s); } catch {}
    return null; // null = noch nie geschrieben (Aufrufer entscheidet über Demo-Fallback)
  };
  const saveCache = (rows) => { try { localStorage.setItem(cacheKey, JSON.stringify(rows)); } catch {} };
  const fromDb = (r) => ({ id: r.id, ...r.data });
  const toDb = (row) => { const { id, ...data } = row; return { id, data }; };

  return {
    loadCache,
    async list() {
      if (!serverActive()) { const rows = loadCache() || []; saveCache(rows); return rows; }
      const { data, error } = await supabase.from(table).select("*").order("id");
      if (error) throw error;
      const rows = data.map(fromDb);
      saveCache(rows);
      return rows;
    },
    async upsert(row) {
      if (!serverActive()) {
        const rows = loadCache() || [];
        const i = rows.findIndex(x => x.id === row.id);
        if (i >= 0) rows[i] = row; else rows.unshift(row);
        saveCache(rows);
        return rows;
      }
      const { error } = await supabase.from(table).upsert(toDb(row), { onConflict: "id" });
      if (error) throw error;
      return this.list();
    },
    async saveAll(rows) {
      if (!serverActive()) { saveCache(rows); return rows; }
      if (rows.length) {
        const { error } = await supabase.from(table).upsert(rows.map(toDb), { onConflict: "id" });
        if (error) throw error;
      }
      return this.list();
    },
  };
}

export const reqStore     = makeDocStore("reqs", "dida_reqs");
export const reviewStore  = makeDocStore("reviews", "dida_reviews");
export const invoiceStore = makeDocStore("invoices", "dida_invoices");
export const teamStore    = makeDocStore("team_members", "dida_team");

// Chat: EIN Dokument (id 1) für den gesamten Verlauf — einfacher Umzug der
// bestehenden Struktur { group:[…], <ziel>:[…] }. Gleichzeitiges Senden von
// zwei Geräten kann sich überschreiben (Pilot-Kompromiss, im Bericht notiert).
export const chatStore = {
  loadCache() {
    try { const s = localStorage.getItem("dida_chat"); if (s) return JSON.parse(s); } catch {}
    return null;
  },
  async load() {
    if (!serverActive()) return this.loadCache();
    const { data, error } = await supabase.from("chat").select("*").eq("id", 1).maybeSingle();
    if (error) throw error;
    const obj = data?.data || null;
    if (obj) { try { localStorage.setItem("dida_chat", JSON.stringify(obj)); } catch {} }
    return obj;
  },
  async save(obj) {
    try { localStorage.setItem("dida_chat", JSON.stringify(obj)); } catch {}
    if (!serverActive()) return;
    const { error } = await supabase.from("chat").upsert({ id: 1, data: obj }, { onConflict: "id" });
    if (error) throw error;
  },
};
